/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ReactNode } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Box, Breadcrumbs, BreadcrumbsList, FlexContainer, Heading, theme } from "@nordcloud/gnui";

type CaptionProps = {
  caption: string;
};

type BreadcrumbsProps = {
  breadcrumbs: BreadcrumbsList[];
};

export type Props = {
  name: string;
  children?: ReactNode;
} & (BreadcrumbsProps | CaptionProps);

export function PageHeader({ name, children, ...props }: Props) {
  return (
    <Box data-cy="page-header" px={theme.spacing.spacing04} py={theme.spacing.spacing03}>
      <FlexContainer justifyContent="space-between">
        <header>
          <Heading level={2} data-cy="page-heading">
            {name}
          </Heading>
          {"breadcrumbs" in props ? (
            <StyledBreadcrumbs>
              <Breadcrumbs list={props.breadcrumbs} Component={CustomLink} />
            </StyledBreadcrumbs>
          ) : (
            <div
              css={{
                fontSize: theme.fontSizes.sm,
                marginBottom: 0,
                color: theme.color.text.text03,
              }}
            >
              {props.caption}
            </div>
          )}
        </header>
        <FlexContainer gap={theme.spacing.spacing04}>{children}</FlexContainer>
      </FlexContainer>
    </Box>
  );
}

function CustomLink({ to, children }: { to: string; children?: ReactNode }) {
  return <Link to={to}>{children}</Link>;
}

const StyledBreadcrumbs = styled.div({
  "& ul li": {
    textTransform: "initial",
  },
});
